import { useCallback, useEffect, useState } from 'react';
import { api } from './lib/api';
import { Dashboard } from './pages/Dashboard';
import { InstallersPage } from './pages/Lists';
import { RegionsPage } from './pages/Regions';
import { UnitsPage } from './pages/Units';
import { UsersPage } from './pages/Users';
import { WorkOrderDetailPage } from './pages/WorkOrderDetail';
import { ChangePassword, Login, type SignedInUser } from './pages/Login';
import { Portal } from './portal/Portal';

/**
 * Routing.
 *
 * Hand-rolled rather than a router dependency: there are a handful of routes
 * and the bundle is served to installers on a phone connection, where every
 * kilobyte is a second of someone standing next to a highway.
 */
function usePathname(): [string, (path: string) => void] {
  const [pathname, setPathname] = useState(window.location.pathname);

  useEffect(() => {
    const onPop = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = (path: string): void => {
    window.history.pushState({}, '', path);
    setPathname(path);
  };

  return [pathname, navigate];
}

type AuthState = { status: 'loading' } | { status: 'out' } | { status: 'in'; user: SignedInUser };

export function App() {
  const [pathname, navigate] = usePathname();
  const [auth, setAuth] = useState<AuthState>({ status: 'loading' });

  // The installer portal is a separate shell with its own token credential:
  // no staff session, no navigation, and it must work for someone who has
  // never seen the app. Checked before any auth work so a crew is never sent
  // to a login screen they have no account for.
  const portalMatch = /^\/i\/(.+)$/.exec(pathname);

  const refresh = useCallback(() => {
    if (portalMatch) return;
    api
      .get<{ user: SignedInUser }>('/api/auth/me')
      .then((d) => setAuth({ status: 'in', user: d.user }))
      // A 401 means "not signed in"; anything else (server down, offline) is
      // also not something the app shell can act on, and the login form is
      // where a real problem will surface legibly.
      .catch(() => setAuth({ status: 'out' }));
  }, [portalMatch]);

  useEffect(refresh, [refresh]);

  if (portalMatch) return <Portal token={decodeURIComponent(portalMatch[1]!)} />;

  if (auth.status === 'loading') {
    return <main className="p-8 text-center text-xs text-neutral-500">Loading…</main>;
  }

  if (auth.status === 'out') {
    return <Login onSignedIn={(user) => setAuth({ status: 'in', user })} />;
  }

  if (auth.user.mustChangePassword) {
    return <ChangePassword onDone={refresh} />;
  }

  const detailMatch = /^\/app\/work-orders\/([^/]+)$/.exec(pathname);

  return (
    <div className="min-h-full">
      <nav className="border-b border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-950">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-1 px-4 py-2">
          <button
            onClick={() => navigate('/app')}
            className="mr-3 bg-black px-2 py-1 text-sm font-bold tracking-tight text-white"
          >
            Harkey<span className="text-harkey-gold">Media</span>
          </button>
          <NavLink current={pathname} to="/app" navigate={navigate} label="Dashboard" />
          <NavLink current={pathname} to="/app/units" navigate={navigate} label="Units" />
          <NavLink current={pathname} to="/app/installers" navigate={navigate} label="Installers" />
          <NavLink current={pathname} to="/app/regions" navigate={navigate} label="Regions" />
          {/* Only admins can manage the team, so only admins are shown the way
              in. The API enforces it regardless — hiding a link is not a
              permission check. */}
          {auth.user.isAdmin && (
            <NavLink current={pathname} to="/app/users" navigate={navigate} label="Users" />
          )}

          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-neutral-500">
              {auth.user.displayName ?? auth.user.username}
            </span>
            <button
              onClick={() => {
                void api.post('/api/auth/logout').finally(() => setAuth({ status: 'out' }));
              }}
              className="border border-neutral-300 px-2 py-0.5 text-xs hover:bg-neutral-50 dark:border-neutral-700 dark:hover:bg-neutral-800"
            >
              Sign out
            </button>
          </div>
        </div>
      </nav>

      <main className="mx-auto max-w-6xl p-4">
        {detailMatch ? (
          <>
            <button
              onClick={() => navigate('/app')}
              className="mb-3 text-xs text-neutral-500 hover:underline"
            >
              ← Back to dashboard
            </button>
            <WorkOrderDetailPage id={detailMatch[1]!} />
          </>
        ) : pathname.startsWith('/app/units') ? (
          <UnitsPage />
        ) : pathname.startsWith('/app/installers') ? (
          <InstallersPage />
        ) : pathname.startsWith('/app/regions') ? (
          <RegionsPage />
        ) : pathname.startsWith('/app/users') ? (
          <UsersPage me={auth.user.username} />
        ) : (
          <Dashboard navigate={navigate} />
        )}
      </main>
    </div>
  );
}

function NavLink({
  current,
  to,
  navigate,
  label,
}: {
  current: string;
  to: string;
  navigate: (p: string) => void;
  label: string;
}) {
  const active = to === '/app' ? current === '/app' || current === '/' : current.startsWith(to);
  return (
    <button
      onClick={() => navigate(to)}
      aria-current={active ? 'page' : undefined}
      className={`px-2.5 py-1 text-xs ${
        active
          ? 'border-b-2 border-harkey-gold font-semibold'
          : 'text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100'
      }`}
    >
      {label}
    </button>
  );
}
